import type {
  FulfillmentLineFilters,
  FulfillmentOrderLine,
  FulfillmentSourceDiff,
  FulfillmentSupplierOption,
  FulfillmentWorkspace,
  SettlementSupplierDraft,
} from "../types";
import { normalizeFulfillmentResourceType } from "../types";
import {
  FULFILLMENT_PRICING_UNIT_OPTIONS,
  FULFILLMENT_RESOURCE_OPTIONS,
  type FulfillmentLineCategory,
} from "../config";

export { normalizeFulfillmentResourceType } from "../types";

const FULFILLMENT_CATEGORY_FILTERS: Readonly<Record<
  FulfillmentLineCategory,
  Pick<FulfillmentLineFilters, "resourceType" | "assignment">
>> = {
  all: { resourceType: "all", assignment: "all" },
  stay: { resourceType: "住宿", assignment: "all" },
  transport: { resourceType: "大交通", assignment: "all" },
  charter: { resourceType: "包车", assignment: "all" },
  ticket: { resourceType: "考察点", assignment: "all" },
  meal: { resourceType: "餐", assignment: "all" },
  guide: { resourceType: "导游", assignment: "all" },
  other: { resourceType: "杂费", assignment: "all" },
  unassigned: { resourceType: "all", assignment: "unassigned" },
};

/** 将计价类型代码转换为中文展示标签，未知值统一归为其他。 */
export function pricingUnitTypeLabel(value: string): string {
  return FULFILLMENT_PRICING_UNIT_OPTIONS.find((option) => option.value === value)?.label
    ?? "其他计价方式";
}

/** 将历史或当前资源类型转换为统一中文展示标签。 */
export function fulfillmentResourceTypeLabel(value: string): string {
  const normalized = normalizeFulfillmentResourceType(value);
  return FULFILLMENT_RESOURCE_OPTIONS.find((option) => option.value === normalized)?.label
    ?? normalized;
}

const FULFILLMENT_SUPPLIER_TYPE_LABELS: Readonly<Record<string, string>> = {
  ground_operator: "地接供应商",
  hotel: "住宿供应商",
  bus_fleet: "车队供应商",
  composite_supplier: "综合服务公司",
  ticket_supplier: "票务供应商",
  guide_partner: "导游供应商",
  地接: "地接供应商",
  地接公司: "地接供应商",
  旅行社: "地接供应商",
  酒店: "住宿供应商",
  住宿: "住宿供应商",
  车队: "车队供应商",
  包车: "车队供应商",
  大巴: "车队供应商",
  票务: "票务供应商",
  导游: "导游供应商",
};

const FULFILLMENT_SUPPLIER_TYPES_BY_RESOURCE: Readonly<Record<
  string,
  readonly string[]
>> = {
  住宿: ["hotel"],
  包车: ["bus_fleet", "ground_operator"],
  大交通: ["composite_supplier"],
  导游: ["guide_partner"],
};

/** 返回资源类型允许的履约供应商类型集合，开放资源返回 null。 */
export function fulfillmentSupplierTypesForResource(
  resourceType: string,
): readonly string[] | null {
  const types = FULFILLMENT_SUPPLIER_TYPES_BY_RESOURCE[
    normalizeFulfillmentResourceType(resourceType)
  ];
  return types ?? null;
}

/** 返回资源类型的首个兼容履约供应商类型，供旧单类型调用方过渡。 */
export function fulfillmentSupplierTypeForResource(resourceType: string): string | null {
  return fulfillmentSupplierTypesForResource(resourceType)?.[0] ?? null;
}

/** 解释当前资源类型没有履约候选的原因；搜索无结果时不误导为主数据缺失。 */
export function fulfillmentSupplierEmptyMessage(
  resourceType: string,
  hasSearchKeyword: boolean,
): string {
  if (hasSearchKeyword) return "未找到匹配的履约供应商，请更换关键词";
  const normalized = normalizeFulfillmentResourceType(resourceType);
  if (normalized === "大交通") {
    return "当前没有可用的综合服务公司，请联系管理员新增或启用供应商";
  }
  if (normalized === "住宿") {
    return "当前没有可用的酒店，请联系管理员新增或启用供应商";
  }
  if (normalized === "包车") {
    return "当前没有可用的车队或地接，请联系管理员新增或启用供应商";
  }
  if (normalized === "导游") {
    return "当前没有可用的个人导游，请联系管理员新增或启用导游档案";
  }
  return "当前没有可用的履约供应商，请联系管理员新增或启用供应商";
}

/** 判断供应商能否用于指定资源；开放资源接受任意履约供应商类型。 */
export function isFulfillmentSupplierCompatibleWithResource(
  supplier: Pick<FulfillmentSupplierOption, "type" | "subjectNature">,
  resourceType: string,
): boolean {
  const normalizedResourceType = normalizeFulfillmentResourceType(resourceType);
  const allowedTypes = fulfillmentSupplierTypesForResource(normalizedResourceType);
  if (allowedTypes !== null && !allowedTypes.includes(supplier.type.trim())) return false;
  return normalizedResourceType !== "导游" || supplier.subjectNature === "individual";
}

const isItineraryDayPlaceholder = (value: string): boolean => {
  const normalized = value.trim();
  return !normalized || /^(?:day\s*\d+|第\s*\d+\s*天)$/iu.test(normalized);
};

/** 把后端行程占位地点转换成不会冒充真实地点的明确空态。 */
export function fulfillmentItineraryLocationSummary(locationSummary: string): string {
  return isItineraryDayPlaceholder(locationSummary) ? "当日地点待补充" : locationSummary;
}

/** 把后端行程占位摘要转换成不会冒充真实路线的明确空态。 */
export function fulfillmentItineraryRouteSummary(routeSummary: string): string {
  if (isItineraryDayPlaceholder(routeSummary)) {
    return "当日暂无行程安排";
  }
  return routeSummary;
}

/** 按当前资源类型同步过滤履约候选，防止旧请求占位数据越界。 */
export function filterFulfillmentSupplierOptionsForResource<
  T extends Pick<
    FulfillmentSupplierOption,
    "type" | "subjectNature" | "canBeFulfillmentSubject"
  >,
>(options: readonly T[], resourceType: string): T[] {
  return options.filter(
    (option) => option.canBeFulfillmentSubject
      && isFulfillmentSupplierCompatibleWithResource(option, resourceType),
  );
}

/** 供应商业务名称至少包含一个 Unicode 字母或数字，纯标点历史值不可用于业务展示。 */
export function isReadableSupplierBusinessName(value: string | null | undefined): boolean {
  const normalized = value?.trim();
  return Boolean(normalized && /[\p{L}\p{N}]/u.test(normalized));
}

/** 判断住宿确认行是否已通过结构化选择或完整四字段关联酒店资源。 */
export function isFulfillmentStayResourceLinked(line: FulfillmentOrderLine): boolean {
  if (normalizeFulfillmentResourceType(line.resourceType) !== "住宿") return false;
  if (line.resourceSelection?.kind === "stay") {
    return Boolean(line.resourceSelection.staySpuId.trim());
  }
  return line.spuTableName === "resource_stay_fulfill_spu"
    && Boolean(line.spuId?.trim())
    && line.skuTableName === "resource_stay_fulfill_sku"
    && Boolean(line.skuId?.trim());
}

/** 返回实际服务方名称；住宿资源直采时以酒店 SPU 名称展示但不伪造供应商 ID。 */
export function fulfillmentServiceProviderName(
  line: FulfillmentOrderLine,
): string | null {
  if (isReadableSupplierBusinessName(line.fulfillmentSupplierName)) {
    return line.fulfillmentSupplierName!.trim();
  }
  const hasSupplierReference = Boolean(
    line.fulfillmentSupplierId?.trim() || line.fulfillmentSupplierName?.trim(),
  );
  if (hasSupplierReference || !isFulfillmentStayResourceLinked(line)) return null;
  return line.spuNameSnapshot?.trim() || line.itemNameSnapshot.trim() || null;
}

/** 生成不包含内部编号或原始类型代码的供应商业务展示标签。 */
export function fulfillmentSupplierOptionLabel(option: FulfillmentSupplierOption): string {
  const businessName = isReadableSupplierBusinessName(option.shortName)
    ? option.shortName!.trim()
    : option.name.trim();
  const rawType = option.type?.trim();
  const supplierType = rawType
    ? FULFILLMENT_SUPPLIER_TYPE_LABELS[rawType] ?? "其他供应商"
    : null;
  return [
    businessName,
    supplierType,
    option.contactName?.trim(),
    option.contactPhone?.trim(),
  ].filter(Boolean).join(" · ");
}

/** 将确认行分类转换为现有资源类型和归口状态过滤器。 */
export function fulfillmentCategoryToFilters(
  category: FulfillmentLineCategory,
  sourceDifferenceOnly: boolean,
): FulfillmentLineFilters {
  return { ...FULFILLMENT_CATEGORY_FILTERS[category], sourceDifferenceOnly };
}

const FULFILLMENT_RESOURCE_CATEGORY: Readonly<Record<string, FulfillmentLineCategory>> = {
  住宿: "stay",
  大交通: "transport",
  包车: "charter",
  考察点: "ticket",
  餐: "meal",
  导游: "guide",
  杂费: "other",
};

/** 从现有确认行过滤器反推当前激活的内部分类值。 */
export function activeFulfillmentCategory(
  filters: FulfillmentLineFilters,
): FulfillmentLineCategory {
  if (filters.assignment === "unassigned") return "unassigned";
  if (filters.assignment !== "all") return "all";
  if (filters.resourceType === "all") return "all";
  return FULFILLMENT_RESOURCE_CATEGORY[
    normalizeFulfillmentResourceType(filters.resourceType)
  ] ?? "all";
}

const travelerRoleLabels: Record<string, string> = { student: "学生", teacher: "老师", guide: "导游", driver: "司机", adult: "成人", child: "儿童" };

/** 仅将角色代码转换为展示标签，已有中文或未知角色保留原值。 */
export function confirmationTravelerRoleLabel(role?: string | null): string {
  return role ? travelerRoleLabels[role] || role : "";
}

/** 使用真实行号、项目名和可选旅客角色标识确认行。 */
export function fulfillmentLineIdentityLabel(line: FulfillmentOrderLine): string {
  const itemName = line.itemNameSnapshot.trim() || "未命名项目";
  const travelerRole = confirmationTravelerRoleLabel(line.travelerRole?.trim());
  return `第 ${line.lineNo} 行（${itemName}${travelerRole ? ` / ${travelerRole}` : ""}）`;
}

/** 解析非负十进制字符串。 */
const MAX_MONEY_CENTS = 999_999_999_999_999_999n;

/** 将非负十进制金额精确换算为分，并按四舍五入处理超出两位的小数。 */
function parseDecimalCents(value: string): bigint {
  const normalized = value.trim();
  const matched = /^(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!matched) throw new Error("数量和金额必须是非负十进制数字");
  const fraction = matched[2] ?? "";
  const denominator = 10n ** BigInt(fraction.length);
  const numerator = BigInt(`${matched[1]}${fraction}`) * 100n;
  let cents = numerator / denominator;
  if ((numerator % denominator) * 2n >= denominator) cents += 1n;
  return cents;
}

/** 使用分为单位计算两个非负金额字符串的差额，避免 Number 精度损失。 */
export function subtractFulfillmentAmounts(minuend: string, subtrahend: string): string {
  const cents = parseDecimalCents(minuend) - parseDecimalCents(subtrahend);
  const sign = cents < 0n ? "-" : "";
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 按数量 × 计费次数 × 单价计算两位 ROUND_HALF_UP 预览金额。 */
export function calculateFulfillmentLineAmount(
  quantity: string,
  billingCount: string,
  unitPriceAmount: string,
): string {
  const factors = [quantity, billingCount, unitPriceAmount].map(parseDecimalCents);
  const numerator = factors.reduce((result, factor) => result * factor, 1n);
  const denominator = 10000n;
  let cents = numerator / denominator;
  if ((numerator % denominator) * 2n >= denominator) cents += 1n;
  return `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
}

/** 使用整数分汇总活动确认行金额，避免 Number 精度污染。 */
export function sumActiveFulfillmentLineAmounts(lines: FulfillmentOrderLine[]): string {
  return sumFulfillmentAmounts(lines
    .filter((line) => line.status === "active")
    .map((line) => line.amount));
}

/** 使用整数分汇总任意非负金额，供确认行与当前应付组合计算。 */
export function sumFulfillmentAmounts(amounts: string[]): string {
  const cents = amounts.reduce((total, amount) => total + parseDecimalCents(amount), 0n);
  return `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
}

/** 判断确认行是否可在草稿工作区编辑。 */
export function isFulfillmentLineEditable(line: FulfillmentOrderLine, workspaceStatus = "draft") {
  return (workspaceStatus === "draft" || workspaceStatus === "ready")
    && line.status === "active"
    && !line.fulfillmentOrderId;
}

/** 组合过滤确认行池。 */
export function filterFulfillmentLines(
  lines: FulfillmentOrderLine[],
  filters: FulfillmentLineFilters,
  sourceDiff?: FulfillmentSourceDiff,
): FulfillmentOrderLine[] {
  const changedSourceIds = new Set(
    sourceDiff?.lines
      .filter((item) => (
        item.changeTypes.length > 0
        || item.diffReasons.length > 0
        || item.diffAmount !== "0.00"
      ))
      .map((item) => item.sourceBudgetLineId) ?? [],
  );
  return lines.filter((line) => {
    if (line.status !== "active") return false;
    if (
      filters.resourceType !== "all"
      && normalizeFulfillmentResourceType(line.resourceType)
        !== normalizeFulfillmentResourceType(filters.resourceType)
    ) return false;
    const assignment = line.fulfillmentOrderId ? "assigned" : "unassigned";
    if (filters.assignment !== "all" && assignment !== filters.assignment) return false;
    if (filters.sourceDifferenceOnly) {
      const hasLineDifference = line.sourceType === "manual"
        || Number(line.diffAmount ?? "0") !== 0
        || Boolean(line.diffReason?.trim())
        || Boolean(line.sourceBudgetLineId && changedSourceIds.has(line.sourceBudgetLineId));
      if (!hasLineDifference) return false;
    }
    return true;
  });
}

/** 计算当前可见且可选行的全选/半选状态。 */
export function deriveVisibleSelectionState(
  visibleLines: FulfillmentOrderLine[],
  selectedIds: ReadonlySet<string>,
) {
  const selectableIds = visibleLines.filter((line) => isFulfillmentLineEditable(line)).map((line) => line.id);
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  return {
    checked: selectableIds.length > 0 && selectedCount === selectableIds.length,
    indeterminate: selectedCount > 0 && selectedCount < selectableIds.length,
    selectableIds,
  };
}

/** 复制拆行；新行保持来源引用但不继承协议归属。 */
export function duplicateFulfillmentLine(
  source: FulfillmentOrderLine,
  localId: string,
): FulfillmentOrderLine {
  return {
    ...source,
    id: localId,
    fulfillmentOrderId: null,
    status: "active",
    diffAmount: null,
    amount: calculateFulfillmentLineAmount(
      source.quantity,
      source.billingCount,
      source.unitPriceAmount,
    ),
  };
}

/** 创建不伪造资源标识、继承工作区服务日期的本地手工确认行。 */
export function createManualFulfillmentLine(
  workspace: FulfillmentWorkspace,
  localId: string,
  lineNo: number,
): FulfillmentOrderLine {
  const quantity = "1.00";
  const billingCount = "1.00";
  const unitPriceAmount = "0.00";
  return {
    id: localId,
    workspaceId: workspace.id,
    fulfillmentOrderId: null,
    sourceBudgetLineId: null,
    sourceType: "manual",
    lineNo,
    resourceType: "杂费",
    spuTableName: null,
    spuId: null,
    spuNameSnapshot: null,
    skuTableName: null,
    skuId: null,
    skuNameSnapshot: null,
    itemNameSnapshot: "",
    itemSpecSnapshot: null,
    fulfillmentSupplierId: null,
    fulfillmentSupplierName: null,
    settlementSupplierId: null,
    settlementSupplierName: null,
    providerAssignmentStatus: "not_applicable",
    serviceDateFrom: workspace.serviceDateFrom,
    serviceDateTo: workspace.serviceDateTo,
    travelerRole: null,
    mealType: null,
    pricingUnitType: "",
    quantity,
    billingCount,
    unit: "",
    unitPriceAmount,
    amount: calculateFulfillmentLineAmount(quantity, billingCount, unitPriceAmount),
    sourceBudgetAmount: null,
    diffAmount: null,
    diffReason: null,
    status: "active",
    remark: null,
  };
}

/** 为选中未归口行批量写入结算与履约主体草稿。 */
export function applySettlementSupplier(
  lines: FulfillmentOrderLine[],
  selectedIds: ReadonlySet<string>,
  supplier: SettlementSupplierDraft,
): FulfillmentOrderLine[] {
  return lines.map((line) => {
    if (!selectedIds.has(line.id) || !isFulfillmentLineEditable(line)) return line;
    return {
      ...line,
      settlementSupplierId: supplier.id,
      settlementSupplierName: supplier.name,
      ...(supplier.fulfillmentSameAsSettlement
        ? {
          fulfillmentSupplierId: supplier.id,
          fulfillmentSupplierName: supplier.name,
        }
        : {}),
    };
  });
}

/** 判断字符串是否为不低于下限的有限十进制数。 */
function isValidDecimal(value: string, minimum: number): boolean {
  const normalized = value.trim();
  const matched = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!matched) return false;
  const significantWhole = matched[1].replace(/^0+(?=\d)/, "");
  if (significantWhole.length > 16) return false;
  const scaled = BigInt(`${significantWhole}${matched[2] ?? ""}`);
  return minimum > 0 ? scaled > 0n : true;
}

/** 严格校验 YYYY-MM-DD 日历日期。 */
export function isValidIsoDate(value: string): boolean {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!matched) return false;
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

/** 只校验本次修改的计费因素，不让未修改的主体、名称或日期阻断行内编辑。 */
export function validateFulfillmentLineFactors(line: Pick<FulfillmentOrderLine, "id" | "quantity" | "billingCount" | "unitPriceAmount">): string[] {
  const errors: string[] = [];
  const isNewLine = !line.id || line.id.startsWith("local-");
  const quantityValid = isValidDecimal(line.quantity, isNewLine ? Number.MIN_VALUE : 0);
  const billingCountValid = isValidDecimal(line.billingCount, Number.MIN_VALUE);
  const unitPriceValid = isValidDecimal(line.unitPriceAmount, 0);
  if (!quantityValid) {
    errors.push(isNewLine
      ? "数量必须是大于 0 的合法十进制数"
      : "数量必须是大于或等于 0 的合法十进制数");
  }
  if (!billingCountValid) {
    errors.push("计费次数必须是大于 0 的合法十进制数");
  }
  if (!unitPriceValid) {
    errors.push("单价必须是大于或等于 0 的合法十进制数");
  }
  if (quantityValid && billingCountValid && unitPriceValid) {
    const amount = calculateFulfillmentLineAmount(
      line.quantity,
      line.billingCount,
      line.unitPriceAmount,
    );
    if (parseDecimalCents(amount) > MAX_MONEY_CENTS) {
      errors.push("金额超出系统可保存范围，请拆分明细");
    }
  }
  return errors;
}

/** 整行保存保留完整字段校验，行内计费只调用因素校验。 */
export function validateFulfillmentLineDraft(line: FulfillmentOrderLine): string[] {
  const errors: string[] = [];
  if (!line.itemNameSnapshot.trim()) errors.push("确认项目不能为空");
  if (!line.pricingUnitType.trim()) errors.push("计价单位类型不能为空");
  errors.push(...validateFulfillmentLineFactors(line));
  if (!line.unit.trim()) errors.push("单位不能为空");
  const from = line.serviceDateFrom?.trim() ?? "";
  const to = line.serviceDateTo?.trim() ?? "";
  if (from && !isValidIsoDate(from)) errors.push("服务开始日期不合法");
  if (to && !isValidIsoDate(to)) errors.push("服务结束日期不合法");
  if (from && to && to < from) errors.push("服务结束日期不得早于开始日期");
  return errors;
}

const ZERO_PRICE_REMARK_KEYWORDS = ["免费", "赠送", "免单"] as const;

/** 正数量零金额生成前须说明免费原因；既有行退减至零数量无须免费备注。 */
export function validateFulfillmentLineBeforeGeneration(
  line: FulfillmentOrderLine,
): string[] {
  const errors = validateFulfillmentLineDraft(line);
  const quantityValid = isValidDecimal(line.quantity, Number.MIN_VALUE);
  const billingCountValid = isValidDecimal(line.billingCount, Number.MIN_VALUE);
  const unitPriceValid = isValidDecimal(line.unitPriceAmount, 0);
  const amountIsZero = quantityValid
    && billingCountValid
    && unitPriceValid
    && parseDecimalCents(calculateFulfillmentLineAmount(
      line.quantity,
      line.billingCount,
      line.unitPriceAmount,
    )) === 0n;
  if (
    amountIsZero
    && !ZERO_PRICE_REMARK_KEYWORDS.some((keyword) => (line.remark ?? "").includes(keyword))
  ) {
    errors.push("零元明细请在备注中说明“免费”“赠送”或“免单”原因");
  }
  return errors;
}

/** 草稿保存只校验可持久化字段，服务主体完整性留到定稿或协议生成。 */
export function validateFulfillmentLinesBeforeSave(lines: FulfillmentOrderLine[]): string[] {
  return lines
    .filter((line) => line.status === "active" && !line.fulfillmentOrderId)
    .flatMap((line) => validateFulfillmentLineDraft(line)
      .map((error) => `${fulfillmentLineIdentityLabel(line)}：${error}`));
}

/** 定稿前核对全部活动行的服务与结算主体，资源住宿沿用真实酒店身份。 */
export function validateFulfillmentLinesBeforeFinalize(lines: FulfillmentOrderLine[]): string[] {
  return lines
    .filter((line) => line.status === "active")
    .flatMap((line) => {
      const prefix = `${fulfillmentLineIdentityLabel(line)}：`;
      const errors: string[] = [];
      if (!isFulfillmentStayResourceLinked(line)
        && (!line.fulfillmentSupplierId?.trim() || !line.fulfillmentSupplierName?.trim())) {
        errors.push("缺少服务供应商");
      }
      if (!line.settlementSupplierId?.trim() || !line.settlementSupplierName?.trim()) {
        errors.push("缺少结算主体");
      }
      return errors.map((error) => `${prefix}${error}`);
    });
}

/** 更新可编辑草稿行并重算本地预览金额；已归口行须由调用方确认其协议仍为草稿。 */
export function patchFulfillmentLine(
  line: FulfillmentOrderLine,
  patch: Partial<FulfillmentOrderLine>,
  allowAssignedDraft = false,
): FulfillmentOrderLine {
  if (!isFulfillmentLineEditable(line) && !(allowAssignedDraft && line.status === "active")) {
    return line;
  }
  const resourceTypeChanged = patch.resourceType !== undefined
    && normalizeFulfillmentResourceType(patch.resourceType) !== normalizeFulfillmentResourceType(line.resourceType);
  const referenceFields = ["spuTableName", "spuId", "skuTableName", "skuId"] as const;
  const hasCompleteReferencePatch = referenceFields.every((field) => (
    Object.prototype.hasOwnProperty.call(patch, field)
  ));
  const next = {
    ...line,
    ...patch,
    ...(resourceTypeChanged && !hasCompleteReferencePatch
      ? {
          spuTableName: null,
          spuId: null,
          skuTableName: null,
          skuId: null,
        }
      : {}),
  };
  return {
    ...next,
    amount: calculateFulfillmentLineAmount(next.quantity, next.billingCount, next.unitPriceAmount),
  };
}
